<template>
  <div>
    <b-card>
      <b-card-title class="mb-0">
        <div class="d-flex justify-content-between align-items-center">
          <h3 class="mb-0">
            Modifier Fournisseur
          </h3>
          <b-button
            variant="outline-primary"
            size="sm"
            :to="{ name: 'supplier-show', params: { id: supplierId }}"
          >
            <feather-icon
              icon="ArrowLeftIcon"
              class="mr-50"
              size="16"
            />
            <span>Retour</span>
          </b-button>
        </div>
      </b-card-title>
    </b-card>

    <b-overlay
      :show="isLoading"
      rounded="sm"
    >
      <b-card>
        <validation-observer
          ref="supplierUpdateForm"
          #default="{ handleSubmit }"
        >
          <b-form @submit.prevent="handleSubmit(onSubmit)">
            <!-- Information de base -->
            <b-card-title>Information de base</b-card-title>
            <b-row>
              <!-- Nom du fournisseur -->
              <b-col md="6">
                <validation-provider
                  #default="{ errors, valid }"
                  name="Nom du fournisseur"
                  rules="required"
                  mode="eager"
                >
                  <b-form-group label-class="form-label-premium"
                    label="Nom du fournisseur"
                    label-for="username"
                  >
                    <div class="position-relative">
                      <b-form-input
                        id="username"
                        v-model="supplierData.username"
                        :state="errors.length > 0 ? false : (valid && supplierData.username ? true : null)"
                        placeholder="Entrez le nom du fournisseur"
                        class="premium-input"
                      />
                      <feather-icon
                        v-if="valid && supplierData.username"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                                <!-- Type de fournisseur -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label-for="is_an_shipment_company"
                  description="Société de livraison partenaire qui nous sous-traite la livraison de ses colis et synchronise ses commandes avec notre plateforme."
                >
                  <template #label>
                    Société de livraison (Sous-traitance)
                  </template>
                  <b-form-checkbox
                    id="is_an_shipment_company"
                    v-model="supplierData.is_an_shipment_company"
                    name="is_an_shipment_company"
                    switch
                    :value="true"
                    :unchecked-value="false"
                  >
                    Marquer comme société de livraison en sous-traitance
                  </b-form-checkbox>
                </b-form-group>
              </b-col>

</b-form-group>
                </validation-provider>
              </b-col>

              <!-- Email -->
              <b-col md="6">
                <validation-provider
                  #default="{ errors, valid }"
                  name="Email"
                  :rules="`required|email|asyncEmail:${supplierId}`"
                  mode="eager"
                >
                  <b-form-group label-class="form-label-premium"
                    label="Email"
                    label-for="email"
                  >
                    <b-form-input
                      id="email"
                      v-model="supplierData.email"
                      type="email"
                      :state="errors.length > 0 ? false : (valid && supplierData.email ? true : null)"
                      placeholder="Entrez l'email"
                      disabled
                      class="premium-input"
                    />
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                    <small class="text-muted d-block mt-1">L'email ne peut pas être modifié</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>

            <!-- Coordonnées -->
            <b-card-title class="mt-2">
              Coordonnées
            </b-card-title>
            <b-row>
              <!-- Téléphone personnel -->
              <b-col md="6">
                <validation-provider
                  #default="{ errors, valid }"
                  name="Téléphone personnel"
                  :rules="`required|tnPhone|asyncPhone:,${supplierId}`"
                  mode="eager"
                >
                  <b-form-group
                    label-for="phone"
                    label-cols-sm="12"
                    label-class="form-label-premium font-weight-bold"
                    description="Numéro de téléphone personnel (8 chiffres, sans espaces)."
                  >
                    <template #label>
                      Téléphone personnel <span
                        class="text-danger"
                        aria-hidden="true"
                      >*</span>
                    </template>
                    <div class="position-relative">
                      <b-form-input
                        id="phone"
                        v-model="supplierData.phone"
                        :state="errors.length > 0 ? false : (valid && supplierData.phone ? true : null)"
                        placeholder="Numéro de téléphone personnel"
                        aria-required="true"
                        :aria-invalid="errors.length > 0 ? 'true' : 'false'"
                        class="premium-input"
                        @keypress="allowOnlyNumbers"
                      />
                      <feather-icon
                        v-if="valid && supplierData.phone"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                      role="alert"
                    >{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Téléphone société -->
              <b-col md="6">
                <b-form-group
                  label="Téléphone société (optionnel)"
                  label-for="phone2"
                  label-cols-sm="12"
                  label-class="form-label-premium font-weight-bold"
                  description="Numéro de téléphone de la société (optionnel)."
                >
                  <b-form-input class="premium-input"
                    id="phone2"
                    v-model="supplierData.phone2"
                    placeholder="Numéro de téléphone de la société"
                    @keypress="allowOnlyNumbers"
                  />
                </b-form-group>
              </b-col>

              <!-- Nom de la page (optionnel) -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label="Nom de la page (Facebook/Instagram, etc.)"
                  label-for="page_name"
                  description="Nom de la page commerciale du fournisseur (optionnel)."
                >
                  <b-form-input class="premium-input"
                    id="page_name"
                    v-model="supplierData.page_name"
                    placeholder="Ex: Ma Boutique Officielle"
                  />
                </b-form-group>
              </b-col>

              <!-- URL du site (optionnel) -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label="URL du site web"
                  label-for="website_url"
                  description="Adresse du site web du fournisseur (optionnel)."
                >
                  <b-form-input class="premium-input"
                    id="website_url"
                    v-model="supplierData.website_url"
                    type="url"
                    placeholder="https://exemple.com"
                  />
                </b-form-group>
              </b-col>

            </b-row>

            <!-- Payment Configuration Card -->
            <b-card-title class="mt-3">
              <div class="d-flex align-items-center">
                <div class="payment-icon-wrapper mr-2">
                  <feather-icon
                    icon="CreditCardIcon"
                    size="20"
                    class="text-primary"
                  />
                </div>
                <div>
                  <h5 class="mb-0">
                    Configuration de paiement
                  </h5>
                  <small class="text-muted">
                    Modalités de règlement et planification des factures
                  </small>
                </div>
              </div>
            </b-card-title>

            <div class="payment-configuration-section p-3 mb-3">
              <b-row>
                <!-- Payment Type -->
                <b-col md="6">
                  <validation-provider
                    #default="{ errors }"
                    name="Type de paiement"
                    rules="required"
                  >
                    <b-form-group label-class="form-label-premium"
                      label="Type de paiement"
                      label-for="payment_type"
                      description="Mode de paiement utilisé pour régler le fournisseur."
                      class="premium-form-group"
                    >
                      <v-select
                        id="payment_type"
                        v-model="supplierData.payment_type"
                        :options="paymentTypeOptions"
                        :reduce="option => option.value"
                        :clearable="false"
                        label="label"
                        placeholder="Sélectionnez un type de paiement"
                        class="premium-select"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>

                <!-- RIB (conditional) -->
                <b-col
                  v-if="supplierData.payment_type === 'virement'"
                  md="6"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="RIB"
                  >
                    <b-form-group label-class="form-label-premium"
                      label="RIB"
                      label-for="rib"
                      description="RIB du fournisseur pour les paiements par virement."
                      class="premium-form-group"
                    >
                      <b-form-input class="premium-input"
                        id="rib"
                        v-model="supplierData.rib"
                        :state="errors.length > 0 ? false : null"
                        placeholder="Entrez le RIB"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>

                <!-- Invoice Generation Days -->
                <b-col
                  cols="12"
                  class="mt-2"
                >
                  <div class="payment-days-section">
                    <div class="d-flex align-items-center mb-2">
                      <feather-icon
                        icon="CalendarIcon"
                        size="16"
                        class="text-primary mr-1"
                      />
                      <label class="mb-0 font-weight-bold">
                        Jours de paiement (Facturation)
                      </label>
                    </div>
                    <small class="text-muted d-block mb-3">
                      Cliquez sur les jours de la semaine prévus pour le règlement et la génération automatique des factures.
                    </small>

                    <!-- Visual Day Picker -->
                    <div class="days-picker-grid">
                      <div
                        v-for="day in weekdayOptions"
                        :key="day.value"
                        :class="[
                          'day-picker-item',
                          { 'selected': isDaySelected(day.value) }
                        ]"
                        @click="toggleDay(day.value)"
                      >
                        <div class="day-icon mb-1">
                          <feather-icon
                            v-if="isDaySelected(day.value)"
                            icon="CheckCircleIcon"
                            size="24"
                            class="text-success"
                          />
                          <feather-icon
                            v-else
                            icon="CircleIcon"
                            size="24"
                            class="text-muted"
                          />
                        </div>
                        <div class="day-label">
                          {{ day.label }}
                        </div>
                      </div>
                    </div>

                    <!-- Summary -->
                    <div class="mt-3">
                      <div
                        v-if="supplierData.invoice_generation_days && supplierData.invoice_generation_days.length > 0"
                        class="selected-summary"
                      >
                        <small class="text-success font-weight-bold">
                          <feather-icon
                            icon="CheckIcon"
                            size="14"
                            class="mr-1"
                          />
                          {{ supplierData.invoice_generation_days.length }} jour(s) sélectionné(s)
                        </small>
                      </div>
                      <div
                        v-else
                        class="text-muted"
                      >
                        <small>
                          <feather-icon
                            icon="InfoIcon"
                            size="14"
                            class="mr-1"
                          />
                          Aucun jour sélectionné (optionnel)
                        </small>
                      </div>
                    </div>
                  </div>
                </b-col>
              </b-row>
            </div>

            <!-- Coordonnées (continued) -->
            <b-row>
              <!-- Adresse -->
              <b-col cols="12">
                <validation-provider
                  #default="{ errors, valid }"
                  name="Adresse"
                  rules="required"
                  mode="eager"
                >
                  <b-form-group
                    label="Adresse complète"
                    label-for="address"
                    label-cols-sm="12"
                    label-class="form-label-premium font-weight-bold"
                    description="Adresse logistique complète du fournisseur."
                  >
                    <div class="position-relative">
                      <b-form-textarea
                        id="address"
                        v-model="supplierData.address"
                        :state="errors.length > 0 ? false : (valid && supplierData.address ? true : null)"
                        placeholder="Adresse complète"
                        rows="3"
                        class="premium-input"
                      />
                      <feather-icon
                        v-if="valid && supplierData.address"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon textarea-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>

            <!-- Informations fiscales & commerciales -->
            <b-card-title class="mt-2">
              Informations fiscales & commerciales
            </b-card-title>
            <b-row>
              <!-- Matricule fiscal -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label="Matricule fiscal"
                  label-for="tax_identification_number"
                  description="Numéro d'identification fiscale du fournisseur."
                >
                  <b-form-input class="premium-input"
                    id="tax_identification_number"
                    v-model="supplierData.tax_identification_number"
                    :state="cinOrMatriculeError ? false : null"
                    placeholder="Entrez le matricule fiscal"
                  />
                </b-form-group>
              </b-col>

              <!-- CIN -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label="CIN"
                  label-for="cin"
                  description="Carte d'identité nationale du fournisseur (8 chiffres)."
                >
                  <validation-provider
                    #default="{ errors, valid }"
                    name="CIN"
                    rules="tnCin"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <b-form-input
                        id="cin"
                        v-model="supplierData.cin"
                        :state="errors.length > 0 ? false : (valid && supplierData.cin && supplierData.cin.length === 8 ? true : null)"
                        placeholder="Entrez le CIN (8 chiffres)"
                        class="premium-input"
                        maxlength="8"
                        @keypress="allowOnlyNumbers"
                      />
                      <feather-icon
                        v-if="valid && supplierData.cin && supplierData.cin.length === 8"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- CIN or Matricule Fiscal validation message -->
              <b-col
                v-if="cinOrMatriculeError"
                cols="12"
              >
                <b-alert
                  variant="warning"
                  show
                  class="mb-2 py-1"
                >
                  <feather-icon
                    icon="AlertTriangleIcon"
                    size="16"
                    class="mr-1"
                  />
                  Veuillez renseigner au moins un des deux champs : Matricule fiscal ou CIN.
                </b-alert>
              </b-col>
            </b-row>

            <b-row>
              <!-- Dépôt -->
              <b-col md="6">
                <validation-provider
                  #default="{ errors, valid }"
                  name="Dépôt"
                  rules="required"
                  mode="eager"
                >
                  <b-form-group label-class="form-label-premium"
                    label="Dépôt"
                    label-for="repository"
                  >
                    <div class="position-relative">
                      <v-select
                        id="repository"
                        v-model="supplierData.repository"
                        :options="repositoryOptions"
                        :reduce="option => option.value"
                        :clearable="false"
                        :state="errors.length > 0 ? false : (valid && supplierData.repository ? true : null)"
                        label="label"
                        placeholder="Sélectionner un dépôt"
                        class="premium-select"
                        :class="{'is-valid': valid && supplierData.repository, 'is-invalid': errors.length > 0}"
                      />
                      <feather-icon
                        v-if="valid && supplierData.repository"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon select-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>

            <!-- Information de tarification -->
            <b-card-title class="mt-2">
              <feather-icon
                icon="TruckIcon"
                class="mr-1"
              />
              Information de tarification
            </b-card-title>

            <b-row>
              <!-- Weight price toggle -->
              <b-col
                md="6"
                class="d-flex align-items-center"
              >
                <b-form-group label-class="form-label-premium" class="mb-0">
                  <b-form-checkbox
                    v-model="supplierData.weight_price"
                    switch
                    class="custom-control-success"
                  >
                    <span class="font-weight-bold">Prix par poids (tarification personnalisée)</span>
                  </b-form-checkbox>
                  <small class="text-muted d-block mt-1">
                    Activer pour configurer les prix en fonction du poids
                  </small>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Standard pricing (when weight_price is false) -->
            <div
              v-if="!supplierData.weight_price"
              class="mt-2"
            >
              <b-row>
                <b-col md="6">
                  <b-form-group label-class="form-label-premium"
                    label="Prix de livraison (TND)"
                    label-for="delivery_price"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Prix de livraison"
                      :rules="!supplierData.weight_price ? 'required|priceDecimal' : ''"
                    >
                      <b-input-group class="input-group-merge">
                        <b-form-input class="premium-input"
                          id="delivery_price"
                          v-model="supplierData.price_delivery"
                          :formatter="formatPrice"
                          type="text"
                          inputmode="decimal"
                          :state="errors.length > 0 ? false : null"
                          placeholder="0.000"
                          @keypress="allowOnlyDecimalNumbers"
                        />
                        <b-input-group-append is-text>
                          <span>TND</span>
                        </b-input-group-append>
                      </b-input-group>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <b-col md="6">
                  <b-form-group label-class="form-label-premium"
                    label="Prix de retour (TND)"
                    label-for="return_price"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Prix de retour"
                      :rules="!supplierData.weight_price ? 'required|priceDecimal' : ''"
                    >
                      <b-input-group class="input-group-merge">
                        <b-form-input class="premium-input"
                          id="return_price"
                          v-model="supplierData.price_return"
                          :formatter="formatPrice"
                          type="text"
                          inputmode="decimal"
                          :state="errors.length > 0 ? false : null"
                          placeholder="0.000"
                          @keypress="allowOnlyDecimalNumbers"
                        />
                        <b-input-group-append is-text>
                          <span>TND</span>
                        </b-input-group-append>
                      </b-input-group>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </div>

            <!-- Weight-based pricing (when weight_price is true) -->
            <div
              v-if="supplierData.weight_price"
              class="mt-3"
            >
              <div class="weight-pricing-header d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0 text-primary">
                  <feather-icon
                    icon="PackageIcon"
                    size="16"
                    class="mr-1"
                  />
                  Configuration des prix par poids
                </h6>
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  size="sm"
                  @click="addWeightItem"
                >
                  <feather-icon
                    icon="PlusIcon"
                    size="12"
                    class="mr-1"
                  />
                  <span>Ajouter une tranche</span>
                </b-button>
              </div>

              <b-alert
                v-if="weightOptions.length === 0"
                variant="warning"
                show
                class="mb-3 d-flex justify-content-between align-items-center"
              >
                <div>
                  <feather-icon
                    icon="AlertTriangleIcon"
                    size="16"
                    class="mr-1"
                  />
                  Aucun poids disponible dans le système.
                </div>
                <b-button
                  variant="warning"
                  size="sm"
                  @click="openQuickCreateWeightModal"
                >
                  <feather-icon
                    icon="PlusIcon"
                    size="12"
                    class="mr-1"
                  />
                  <span>Créer un poids</span>
                </b-button>
              </b-alert>

              <div
                v-else
                class="weight-pricing-container"
              >
                <b-card
                  v-for="(item, index) in weightItems"
                  :key="index"
                  class="weight-item-card mb-3"
                  no-body
                >
                  <b-card-body class="p-3">
                    <div class="d-flex justify-content-between align-items-start mb-2">
                      <h6 class="mb-0 text-secondary">
                        Poids {{ index + 1 }}
                      </h6>
                      <b-button
                        v-if="weightItems.length > 1"
                        variant="outline-danger"
                        size="sm"
                        @click="removeWeightItem(index)"
                      >
                        <feather-icon
                          icon="TrashIcon"
                          size="14"
                        />
                      </b-button>
                    </div>

                    <b-row>
                      <!-- Weight Selection -->
                      <b-col
                        md="12"
                        class="mb-3"
                      >
                        <b-form-group label-class="form-label-premium"
                          label-for="weight"
                          class="mb-0"
                        >
                          <template #label>
                            <div class="d-flex justify-content-between align-items-center">
                              <span>Sélectionnez un poids <span class="text-danger">*</span></span>
                              <b-button
                                variant="link"
                                size="sm"
                                class="p-0 text-primary font-weight-normal"
                                @click="openQuickCreateWeightModal"
                              >
                                <feather-icon
                                  icon="PlusIcon"
                                  size="12"
                                  class="mr-1"
                                />
                                <span>Nouveau poids</span>
                              </b-button>
                            </div>
                          </template>
                          <validation-provider
                            #default="{ errors }"
                            name="Poids"
                            rules="required"
                          >
                            <v-select
                              v-model="item.weight"
                              :options="weightOptions"
                              :get-option-label="option => option.weight"
                              :get-option-key="option => option.id"
                              placeholder="Choisissez un poids..."
                              :clearable="true"
                              :state="errors.length > 0 ? false : null"
                              class="weight-select"
                            >
                              <template #option="option">
                                <div class="weight-option">
                                  <div class="d-flex justify-content-between align-items-start">
                                    <div class="weight-info">
                                      <div class="font-weight-medium text-primary">
                                        {{ option.weight }}
                                      </div>
                                      <div class="weight-range">
                                        <small class="text-muted">
                                          <span v-if="option.min && option.max">
                                            {{ option.min }} - {{ option.max }} {{ option.unit }}
                                          </span>
                                          <span v-else-if="option.min">
                                            À partir de {{ option.min }} {{ option.unit }}
                                          </span>
                                          <span v-else-if="option.max">
                                            Jusqu'à {{ option.max }} {{ option.unit }}
                                          </span>
                                          <span v-else>
                                            {{ option.unit }}
                                          </span>
                                        </small>
                                      </div>
                                      <div
                                        v-if="option.description"
                                        class="weight-description mt-1"
                                      >
                                        <small class="text-muted">{{ option.description }}</small>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </template>
                              <template #selected-option="option">
                                <div class="d-flex justify-content-between align-items-center">
                                  <span class="font-weight-medium">{{ option.weight }}</span>
                                  <small class="text-muted ml-2">
                                    <span v-if="option.min && option.max">
                                      ({{ option.min }}-{{ option.max }} {{ option.unit }})
                                    </span>
                                    <span v-else>
                                      ({{ option.unit }})
                                    </span>
                                  </small>
                                </div>
                              </template>
                            </v-select>
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </b-form-group>
                      </b-col>

                      <!-- Delivery Price -->
                      <b-col md="6">
                        <b-form-group label-class="form-label-premium"
                          label="Prix de livraison (TND)"
                          label-for="item_delivery_price"
                        >
                          <validation-provider
                            #default="{ errors }"
                            name="Prix de livraison"
                            rules="required|priceDecimal"
                          >
                            <b-input-group class="input-group-merge">
                              <b-form-input class="premium-input"
                                :id="`item_delivery_price_${index}`"
                                v-model="item.price_delivery"
                                :formatter="formatPrice"
                                type="text"
                                inputmode="decimal"
                                :state="errors.length > 0 ? false : null"
                                placeholder="0.000"
                                @keypress="allowOnlyDecimalNumbers"
                              />
                              <b-input-group-append is-text>
                                <span>TND</span>
                              </b-input-group-append>
                            </b-input-group>
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </b-form-group>
                      </b-col>

                      <!-- Return Price -->
                      <b-col md="6">
                        <b-form-group label-class="form-label-premium"
                          label="Prix de retour (TND)"
                          label-for="item_return_price"
                        >
                          <validation-provider
                            #default="{ errors }"
                            name="Prix de retour"
                            rules="required|priceDecimal"
                          >
                            <b-input-group class="input-group-merge">
                              <b-form-input class="premium-input"
                                :id="`item_return_price_${index}`"
                                v-model="item.price_return"
                                :formatter="formatPrice"
                                type="text"
                                inputmode="decimal"
                                :state="errors.length > 0 ? false : null"
                                placeholder="0.000"
                                @keypress="allowOnlyDecimalNumbers"
                              />
                              <b-input-group-append is-text>
                                <span>TND</span>
                              </b-input-group-append>
                            </b-input-group>
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </b-form-group>
                      </b-col>
                    </b-row>
                  </b-card-body>
                </b-card>
              </div>
            </div>

            <!-- Statut du compte -->
            <b-card-title class="mt-2">
              Statut du compte
            </b-card-title>
            <b-form-group label-class="form-label-premium">
              <b-form-checkbox
                v-model="supplierData.is_active"
                name="is_active"
                switch
              >
                {{ supplierData.is_active ? 'Compte actif' : 'Compte inactif' }}
              </b-form-checkbox>
              <small class="text-muted">Si désactivé, ce fournisseur ne pourra pas se connecter ou être utilisé dans le système.</small>
            </b-form-group>

            <!-- Boutons de soumission -->
            <div class="d-flex justify-content-between mt-4">
              <b-button
                variant="outline-secondary"
                :to="{ name: 'supplier-list' }"
              >
                Annuler
              </b-button>
              <span id="supplier-edit-save-btn-wrapper" class="d-inline-block">
                <b-button
                  type="submit"
                  variant="primary"
                  :class="{ 'not-allowed': isSubmitting || !isSupplierValid }"
                  :disabled="isSubmitting || !isSupplierValid"
                >
                  <b-spinner
                    v-if="isSubmitting"
                    small
                    class="mr-50"
                  />
                  <feather-icon
                    v-else
                    icon="SaveIcon"
                    class="mr-50"
                  />
                  <span>{{ isSubmitting ? 'Enregistrement...' : 'Enregistrer' }}</span>
                </b-button>
              </span>
              <b-tooltip
                v-if="!isSupplierValid"
                target="supplier-edit-save-btn-wrapper"
                triggers="hover"
                placement="top"
              >
                {{ saveButtonTooltipMessage }}
              </b-tooltip>
            </div>
          </b-form>
        </validation-observer>
      </b-card>
    </b-overlay>
    <quick-create-weight-modal
      ref="quickCreateWeightModal"
      @weight-created="handleWeightCreated"
    />
  </div>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BOverlay,
  BAlert,
  BInputGroup,
  BInputGroupAppend,
  BTooltip,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import { required, email } from '@validations'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import QuickCreateWeightModal from '@/components/modals/QuickCreateWeightModal.vue'

export default {
  components: {
    BCard,
    BCardTitle,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BOverlay,
    BAlert,
    BInputGroup,
    BInputGroupAppend,
    BTooltip,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    QuickCreateWeightModal,
  },

  data() {
    return {
      required,
      email,
      supplierId: this.$route.params.id,
      isLoading: false,
      isSubmitting: false,
      supplierData: {
        username: '',
        is_an_shipment_company: false,
        email: '',
        phone: '',
        phone2: '',
        page_name: '',
        website_url: '',
        payment_type: 'espece',
        rib: '',
        invoice_generation_days: [],
        address: '',
        tax_identification_number: '',
        cin: '',
        repository: null,
        price_delivery: 0,
        price_return: 0,
        weight_price: false,
        is_active: true,
      },
      repositoryOptions: [],
      paymentTypeOptions: [
        { label: 'Espèce', value: 'espece' },
        { label: 'Virement', value: 'virement' },
      ],
      weekdayOptions: [
        { label: 'Lundi', value: 'monday' },
        { label: 'Mardi', value: 'tuesday' },
        { label: 'Mercredi', value: 'wednesday' },
        { label: 'Jeudi', value: 'thursday' },
        { label: 'Vendredi', value: 'friday' },
        { label: 'Samedi', value: 'saturday' },
        { label: 'Dimanche', value: 'sunday' },
      ],
      weightOptions: [],
      weightItems: [
        {
          weight: null,
          price_delivery: '',
          price_return: '',
        },
      ],
    }
  },

  computed: {
    cinOrMatriculeError() {
      // Returns true if neither CIN nor matricule fiscal is filled
      const hasCin = this.supplierData.cin && this.supplierData.cin.trim() !== ''
      const hasMatricule = this.supplierData.tax_identification_number && this.supplierData.tax_identification_number.trim() !== ''
      return !hasCin && !hasMatricule
    },
    saveButtonTooltipMessage() {
      if (this.isSubmitting) return 'Enregistrement en cours...'

      const missingGeneral = []
      if (!this.supplierData.username || !this.supplierData.username.trim()) missingGeneral.push('Nom')
      if (!this.supplierData.phone || !this.supplierData.phone.trim()) missingGeneral.push('Téléphone')
      if (!this.supplierData.address || !this.supplierData.address.trim()) missingGeneral.push('Adresse')

      const missingIdentification = this.cinOrMatriculeError

      const missingDelivery = []
      if (!this.supplierData.repository) missingDelivery.push('Dépôt')
      if (!this.supplierData.weight_price) {
        if (this.supplierData.price_delivery === '' || this.supplierData.price_delivery === null || this.supplierData.price_delivery < 0) {
          missingDelivery.push('Prix de livraison')
        }
        if (this.supplierData.price_return === '' || this.supplierData.price_return === null || this.supplierData.price_return < 0) {
          missingDelivery.push('Prix de retour')
        }
      } else {
        const hasValidWeight = this.weightItems.some(item => item.weight && item.price_delivery >= 0 && item.price_return >= 0)
        if (!hasValidWeight) missingDelivery.push('Prix par poids')
      }

      const allMissing = []
      if (missingGeneral.length > 0) allMissing.push(...missingGeneral)
      if (missingIdentification) allMissing.push('Matricule Fiscal ou CIN')
      if (missingDelivery.length > 0) allMissing.push(...missingDelivery)

      if (allMissing.length > 0) {
        if (allMissing.length === 1) {
          return `Veuillez renseigner le champ : ${allMissing[0]}.`
        }
        if (allMissing.length <= 3) {
          return `Veuillez remplir les champs obligatoires : ${allMissing.join(', ')}.`
        }
        return `Veuillez remplir les champs obligatoires manquants (${allMissing.length} champs).`
      }

      return 'Veuillez corriger les erreurs dans le formulaire.'
    },
    isSupplierValid() {
      const basicFieldsValid = (
        this.supplierData.username !== ''
        && this.supplierData.phone !== ''
        && this.supplierData.address !== ''
        && this.supplierData.repository !== ''
        && this.supplierData.repository !== null
      )

      if (!basicFieldsValid) return false

      if (this.cinOrMatriculeError) return false

      if (!this.supplierData.weight_price) {
        return this.supplierData.price_delivery >= 0 && this.supplierData.price_return >= 0
      }

      return this.weightItems.some(item => item.weight
        && item.price_delivery >= 0
        && item.price_return >= 0)
    },
  },

  watch: {
    // Watch for changes in pricing mode
    'supplierData.weight_price': {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.handlePricingModeChange(newValue)
        }
      },
    },
    'supplierData.payment_type': function handlePaymentTypeChange(newValue) {
      if (newValue !== 'virement') {
        this.supplierData.rib = ''
      }
    },
  },

  created() {
    this.loadRepositories()
    this.initializeData()
  },

  methods: {
    openQuickCreateWeightModal() {
      if (this.$refs.quickCreateWeightModal) {
        this.$refs.quickCreateWeightModal.show()
      }
    },
    async handleWeightCreated(newWeight) {
      await this.loadWeightOptions()
      const emptyItem = this.weightItems.find(item => !item.weight)
      if (emptyItem) {
        emptyItem.weight = newWeight
      } else {
        this.weightItems.push({
          weight: newWeight,
          price_delivery: '',
          price_return: '',
        })
      }
    },
    isDaySelected(dayValue) {
      return this.supplierData.invoice_generation_days && this.supplierData.invoice_generation_days.includes(dayValue)
    },
    toggleDay(dayValue) {
      if (!this.supplierData.invoice_generation_days) {
        this.supplierData.invoice_generation_days = []
      }
      const index = this.supplierData.invoice_generation_days.indexOf(dayValue)
      if (index > -1) {
        this.supplierData.invoice_generation_days.splice(index, 1)
      } else {
        this.supplierData.invoice_generation_days.push(dayValue)
      }
    },
    getWeekdayLabel(dayValue) {
      const option = this.weekdayOptions.find(opt => opt.value === dayValue)
      return option ? option.label : dayValue
    },
    allowOnlyNumbers(event) {
      const { charCode, target } = event
      // Only allow digits
      if (charCode < 48 || charCode > 57) {
        event.preventDefault()
      }
      // Prevent more than 8 digits
      if (target.id === 'phone' && this.supplierData.phone && this.supplierData.phone.length >= 8) {
        event.preventDefault()
      }
      if (target.id === 'phone2' && this.supplierData.phone2 && this.supplierData.phone2.length >= 8) {
        event.preventDefault()
      }
    },
    handlePricingModeChange(isWeightPricing) {
      if (isWeightPricing) {
        // Switching to weight-based pricing
        this.resetToWeightPricing()
      } else {
        // Switching to fixed pricing
        this.resetToFixedPricing()
      }
    },

    resetToWeightPricing() {
      // Clear fixed pricing values to avoid confusion
      this.supplierData.price_delivery = 0
      this.supplierData.price_return = 0

      // Initialize with one empty weight item if none exist
      if (this.weightItems.length === 0 || !this.weightItems[0].weight) {
        this.weightItems = [
          {
            weight: null,
            price_delivery: '',
            price_return: '',
          },
        ]
      }

      this.showToast('info', 'Mode tarification par poids activé. Configurez les prix pour chaque catégorie de poids.')
    },

    resetToFixedPricing() {
      // Clear weight items to avoid confusion
      this.weightItems = [
        {
          weight: null,
          price_delivery: '',
          price_return: '',
        },
      ]

      // Keep existing fixed prices (including 0) when switching back to fixed pricing

      this.showToast('info', 'Mode prix fixe activé. Configurez les prix de livraison et de retour.')
    },

    async initializeData() {
      // Load weight options first
      await this.loadWeightOptions()
      // Then load supplier data which will set the selected weights
      await this.loadSupplierData()
    },

    async loadRepositories() {
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositoryOptions = data.map(repo => ({
          label: repo.name,
          value: repo.id,
        }))
      } catch (error) {
        this.showToast('danger', 'Erreur lors du chargement des dépôts')
      }
    },

    async loadWeightOptions() {
      try {
        const { data } = await axios.get('/api/weights/')

        // Handle different response formats
        if (Array.isArray(data)) {
          this.weightOptions = data
        } else if (data.results && Array.isArray(data.results)) {
          this.weightOptions = data.results
        } else {
          this.weightOptions = []
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading weight options:', error)
        this.showToast('danger', 'Erreur lors du chargement des catégories de poids')
        this.weightOptions = [] // Ensure it's an empty array on error
      }
    },

    async loadSupplierData() {
      this.isLoading = true
      try {
        const { data } = await axios.get(`/api/auth/users/detail/${this.supplierId}/`)

        // Map API response to form data
        this.supplierData = {
          username: data.username || '',
          is_an_shipment_company: !!data.is_an_shipment_company,
          email: data.email || '',
          phone: data.phone || '',
          phone2: data.phone2 || '',
          page_name: data.page_name || '',
          website_url: data.website_url || '',
          payment_type: data.payment_type || 'espece',
          rib: data.rib || '',
          invoice_generation_days: Array.isArray(data.invoice_generation_days) ? data.invoice_generation_days : [],
          address: data.address || '',
          tax_identification_number: data.tax_identification_number || '',
          cin: data.cin || '',
          repository: data.repository?.id || null,
          price_delivery: data.price_delivery || 0,
          price_return: data.price_return || 0,
          weight_price: !!data.weight_price,
          is_active: data.is_active !== false,
        }

        // Initialize weight items if the supplier has weight pricing enabled
        if (data.weight_price && data.prices && Array.isArray(data.prices) && data.prices.length > 0) {
          // Map existing prices to weight items format
          this.weightItems = data.prices.map(priceItem => ({
            weight: priceItem.weight,
            price_delivery: priceItem.delivery_price || priceItem.price_delivery || '',
            price_return: priceItem.return_price || priceItem.price_return || '',
          }))
        } else if (data.weight_price && data.weights && Array.isArray(data.weights) && data.weights.length > 0) {
          // Fallback: Map existing weights to weight items format (without individual pricing)
          this.weightItems = data.weights.map(weight => ({
            weight,
            price_delivery: data.price_delivery || '',
            price_return: data.price_return || '',
          }))
        } else if (data.weight_price) {
          // Weight pricing enabled but no existing weights, start with one empty item
          this.weightItems = [
            {
              weight: null,
              price_delivery: '',
              price_return: '',
            },
          ]
        } else {
          // Fixed pricing, start with one empty item for potential future use
          this.weightItems = [
            {
              weight: null,
              price_delivery: '',
              price_return: '',
            },
          ]
        }
      } catch (error) {
        this.showToast('danger', 'Erreur lors du chargement des informations du fournisseur')
        // eslint-disable-next-line no-console
        console.error('Error loading supplier data:', error)
      } finally {
        this.isLoading = false
      }
    },

    async onSubmit() {
      this.isSubmitting = true

      try {
        // Validate CIN or Matricule Fiscal
        if (this.cinOrMatriculeError) {
          this.showToast('warning', 'Veuillez renseigner au moins un des deux champs : Matricule fiscal ou CIN.')
          this.isSubmitting = false
          return
        }

        // Helper to parse price numbers or strings safely
        const parsePrice = val => {
          if (val === null || val === undefined || val === '') return null
          const normalized = String(val).replace(',', '.')
          const num = parseFloat(normalized)
          return Number.isNaN(num) ? val : num
        }

        // Prepare the data for submission
        const supplierUpdateData = {
          ...this.supplierData,
          rib: this.supplierData.payment_type === 'virement' ? this.supplierData.rib : '',
        }

        // Validation based on pricing mode
        if (this.supplierData.weight_price) {
          // Weight-based pricing validation
          if (this.weightItems.length === 0) {
            this.showToast('warning', 'Veuillez ajouter au moins un poids pour la tarification par poids.')
            this.isSubmitting = false
            return
          }

          // Validate each weight item
          for (let i = 0; i < this.weightItems.length; i += 1) {
            const item = this.weightItems[i]
            const delPrice = parsePrice(item.price_delivery)
            const retPrice = parsePrice(item.price_return)
            if (!item.weight) {
              this.showToast('warning', `Veuillez sélectionner un poids pour l'élément ${i + 1}.`)
              this.isSubmitting = false
              return
            }
            if (delPrice === '' || delPrice === null || delPrice < 0) {
              this.showToast('warning', `Veuillez renseigner un prix de livraison valide pour l'élément ${i + 1}.`)
              this.isSubmitting = false
              return
            }
            if (retPrice === '' || retPrice === null || retPrice < 0) {
              this.showToast('warning', `Veuillez renseigner un prix de retour valide pour l'élément ${i + 1}.`)
              this.isSubmitting = false
              return
            }
            item.price_delivery = delPrice
            item.price_return = retPrice
          }

          // Prepare weights data for submission
          const weightsIds = this.weightItems.map(item => item.weight.id)
          supplierUpdateData.weights = weightsIds
          supplierUpdateData.prices = this.weightItems

          // Clear fixed pricing fields when using weight pricing
          supplierUpdateData.price_delivery = null
          supplierUpdateData.price_return = null
        } else {
          // Fixed pricing validation
          const delPrice = parsePrice(this.supplierData.price_delivery)
          const retPrice = parsePrice(this.supplierData.price_return)
          if (delPrice === '' || delPrice === null || delPrice < 0) {
            this.showToast('warning', 'Veuillez renseigner un prix de livraison valide pour la tarification fixe.')
            this.isSubmitting = false
            return
          }
          if (retPrice === '' || retPrice === null || retPrice < 0) {
            this.showToast('warning', 'Veuillez renseigner un prix de retour valide pour la tarification fixe.')
            this.isSubmitting = false
            return
          }
          supplierUpdateData.price_delivery = delPrice
          supplierUpdateData.price_return = retPrice

          // Clear weights and prices arrays for fixed pricing
          supplierUpdateData.weights = []
          supplierUpdateData.prices = []
        }

        // Remove email field to prevent email update attempts
        delete supplierUpdateData.email

        // Send update request
        await axios.put(`/api/auth/users/update/${this.supplierId}/`, supplierUpdateData)

        this.showToast('success', 'Fournisseur modifié avec succès')

        // Navigate back to supplier details
        this.$router.push({ name: 'supplier-show', params: { id: this.supplierId } })
      } catch (error) {
        const errorMsg = error.response?.data?.message || 'Une erreur est survenue lors de la mise à jour'
        this.showToast('danger', errorMsg)
        // eslint-disable-next-line no-console
        console.error('Error updating supplier:', error)
      } finally {
        this.isSubmitting = false
      }
    },

    addWeightItem() {
      this.weightItems.push({
        weight: null,
        price_delivery: '',
        price_return: '',
      })
    },

    removeWeightItem(index) {
      if (this.weightItems.length > 1) {
        this.weightItems.splice(index, 1)
      }
    },

    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: variant === 'success' ? 'Succès' : 'Erreur',
          icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertCircleIcon',
          text,
          variant,
        },
      }, {
        position: 'top-right',
        timeout: 5000,
      })
    },
    // Placeholder for future role change logic
    onRoleChange() {
      // To be implemented
    },
    allowOnlyDecimalNumbers(event) {
      const { key, charCode, target } = event
      const currentValue = (target && target.value) || ''

      const isDigit = charCode >= 48 && charCode <= 57
      const isDecimalSeparator = key === '.' || key === ',' || charCode === 46 || charCode === 44

      if (!isDigit && !isDecimalSeparator) {
        event.preventDefault()
        return
      }

      if (isDecimalSeparator && (currentValue.includes('.') || currentValue.includes(','))) {
        event.preventDefault()
      }
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return value
      let cleaned = String(value).replace(',', '.').replace(/[^0-9.]/g, '')
      const firstSepIndex = cleaned.indexOf('.')
      if (firstSepIndex !== -1) {
        const main = cleaned.slice(0, firstSepIndex + 1)
        const rest = cleaned.slice(firstSepIndex + 1).replace(/\./g, '')
        cleaned = main + rest
      }
      return cleaned
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.vs__dropdown-toggle {
  padding: 4px 0;
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
  height: 40px;
}

.vs__selected {
  margin: 2px 4px 0 4px;
}

.vs--single .vs__selected {
  padding-top: 2px;
}

// Premium Payment Configuration Styles
.payment-icon-wrapper {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e2eefa 0%, #f2f7fc 100%);
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(13, 67, 153, 0.1);
}

.payment-configuration-section {
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border: 1px solid #e2eefa;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(13, 67, 153, 0.04);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 15px rgba(13, 67, 153, 0.08);
  }
}

.payment-days-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1.25rem;
  transition: all 0.2s ease;

  label {
    color: #334155;
    letter-spacing: 0.3px;
  }
}

.days-picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.day-picker-item {
  background: #ffffff;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  padding: 1rem 0.75rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    border-color: #ff0000;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(13, 67, 153, 0.15);
  }

  &.selected {
    background: linear-gradient(135deg, #e2eefa 0%, #f2f7fc 100%);
    border-color: #ff0000;
    border-width: 2px;

    .day-label {
      color: #ff0000;
      font-weight: 700;
    }

    &:hover {
      box-shadow: 0 6px 16px rgba(13, 67, 153, 0.25);
    }
  }

  .day-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 32px;
  }

  .day-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #64748b;
    transition: all 0.2s ease;
  }
}

.selected-summary {
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  display: inline-flex;
  align-items: center;
}

.premium-form-group {
  label {
    color: #334155;
    font-weight: 600;
    letter-spacing: 0.3px;
    margin-bottom: 0.5rem;
  }
}

.premium-select {
  .vs__dropdown-toggle {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    min-height: 42px;
    transition: all 0.2s ease;

    &:hover {
      border-color: #ff0000;
    }
  }

  &.vs--open .vs__dropdown-toggle {
    border-color: #ff0000;
    box-shadow: 0 0 0 3px rgba(13, 67, 153, 0.15);
  }

  .vs__selected {
    background: #e2eefa;
    color: #ff0000;
    border-radius: 6px;
    padding: 4px 8px;
    font-weight: 500;
    font-size: 0.875rem;
  }

  .vs__deselect {
    fill: #ff0000;
  }
}

// Add additional styling for weight selection table
.table-responsive {
  max-height: 400px;
  overflow-y: auto;

  .table {
    thead th {
      position: sticky;
      top: 0;
      background: white;
      z-index: 1;
      box-shadow: 0 2px 3px rgba(0,0,0,0.1);
    }
  }
}

.card-header h6 {
  font-weight: 600;
  color: #5e5873;
}

.btn.not-allowed,
.btn:disabled {
  cursor: not-allowed !important;
  opacity: 0.6 !important;
  pointer-events: auto !important;
}

// Premium Input Styling with Real-time Validation
.premium-input {
  transition: all 0.3s ease;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;

  &:focus {
    border-color: #ff0000;
    box-shadow: 0 0 0 3px rgba(13, 67, 153, 0.15);
  }

  &.is-valid {
    border-color: #28c76f !important;
    background-color: #f0fdf4;
    padding-right: 3rem;

    &:focus {
      border-color: #28c76f !important;
      box-shadow: 0 0 0 3px rgba(40, 199, 111, 0.15);
    }
  }

  &.is-invalid {
    border-color: #ea5455 !important;
    background-color: #fef2f2;

    &:focus {
      border-color: #ea5455 !important;
      box-shadow: 0 0 0 3px rgba(234, 84, 85, 0.15);
    }
  }
}

.input-success-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #28c76f;
  pointer-events: none;
  z-index: 5;
  animation: checkFadeIn 0.3s ease;

  &.textarea-icon {
    top: 1rem;
    transform: none;
  }

  &.select-icon {
    right: 3rem;
  }
}

@keyframes checkFadeIn {
  from {
    opacity: 0;
    transform: translateY(-50%) scale(0.5);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
}

.validation-message-animate {
  display: inline-flex;
  align-items: center;
  animation: fadeInDown 0.3s ease;
  margin-top: 0.25rem;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// V-Select Real-time Validation
.premium-select {
  &.is-valid .vs__dropdown-toggle {
    border: 2px solid #28c76f !important;
    background-color: #f0fdf4;
  }

  &.is-invalid .vs__dropdown-toggle {
    border: 2px solid #ea5455 !important;
    background-color: #fef2f2;
  }
}
</style>
